<script lang="ts">
  import { onMount, afterUpdate } from "svelte";
  import { push } from "svelte-spa-router";
  import Button from "../../Button.svelte";
  import Table from "../../Table.svelte";
  import Pagination from "../../Pagination.svelte";
  import Dropdown from "../../Dropdown.svelte";
  import DateRange from "../../DateRange.svelte";
  import DonationRequestModal from "../myDonations/DonationRequestModal.svelte";
  import {
    stageFromStatusName,
    STATUS_BUCKET_LABEL,
    STATUS_BUCKET_LIFECYCLE_STAGES,
    isApprovedValueStage,
    statusBucketBadgeHtmlFromRow,
    type DonationStatusBucket,
    type LifecycleStage,
  } from "../../../lib/donationLifecycle";
  import { AuthService } from "../../../lib/api/auth.service";
  import {
    EmployeeDashboardService,
    type DashboardData,
  } from "../../../lib/api/employee/dashboard.service";
  import EmployeeDonationService, {
    type DonationRequest,
    type DonationRequestFilters,
    type Employer,
  } from "../../../lib/api/employee/donation.service";
  import { MyEmployerService } from "../../../lib/api/employee/myEmployer.service";

  const statusBuckets: DonationStatusBucket[] = ['requested', 'approved', 'rejected'];
  const statusFilterOptions = [
    { uuid: '', name: 'All Statuses' },
    ...statusBuckets.map((bucket) => ({ uuid: bucket, name: STATUS_BUCKET_LABEL[bucket] })),
  ];

  // State variables
  let loading = true;
  let error: string | null = null;
  let dashboardData: DashboardData | null = null;
  let showDonationModal = false;
  let user: { uuid: string; full_name?: string } | null = null;

  // Donation list state
  let donationsLoading = false;
  let donations: DonationRequest[] = [];
  let filteredDonations: any[] = [];
  let donationsCurrentPage = 1;
  let donationsTotalPages = 1;
  let donationsTotalItems = 0;

  // Filter states
  let selectedStatusBucket: DonationStatusBucket | '' = '';
  let selectedEmployer = '';
  let startDate = '';
  let endDate = '';

  // Data options
  let employerOptions: Array<{ uuid: string; name: string }> = [];

  function formatHours(hours: number): string {
    const rounded = Number.isInteger(hours) ? hours : Math.round(hours * 10) / 10;
    return `${rounded}hrs`;
  }

  function formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 0,
    }).format(value);
  }

  function formatBucketValue(bucket: DonationStatusBucket): string {
    const buckets = dashboardData?.summary.statusBuckets;
    const data = buckets?.[bucket];
    if (!data) return formatHours(0);

    if (bucket === 'approved') {
      return `${formatHours(data.hours)} (${formatCurrency(data.value ?? 0)})`;
    }

    return formatHours(data.hours);
  }

  function handleStatusBucketClick(bucket: DonationStatusBucket) {
    selectedStatusBucket = selectedStatusBucket === bucket ? '' : bucket;
    donationsCurrentPage = 1;
    fetchDonationRequests();
  }

  function handleStatusFilterChange(value: string) {
    selectedStatusBucket = (value || '') as DonationStatusBucket | '';
    donationsCurrentPage = 1;
    fetchDonationRequests();
  }

  function formatTableValue(row: {
    lifecycle_stage?: string;
    status?: string;
    leave_value?: number;
  }): string {
    const stage = (row.lifecycle_stage as LifecycleStage) || stageFromStatusName(row.status);
    if (!isApprovedValueStage(stage)) {
      return '<span class="value-empty">—</span>';
    }
    return `<span class="value-amount">${formatCurrency(row.leave_value ?? 0)}</span>`;
  }


  function handleCardClick(donation: any) {
    // Navigate to individual donation request view when card is clicked
    if (donation.uuid) {
      push(`/my-donation/${donation.uuid}`);
    }
  }

  function formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    } catch {
      return dateString;
    }
  }

  function handleCreateDonation() {
    showDonationModal = true;
  }

  function handleModalClose() {
    showDonationModal = false;
  }

  // Load employers for dropdown
  async function loadEmployers() {
    if (!user) return;
    
    try {
      const result = await MyEmployerService.getEmployers(user.uuid);
      if (result.success && result.data && Array.isArray(result.data)) {
        employerOptions = result.data.map((employer: Employer) => ({
          uuid: employer.uuid,
          name: employer.name
        }));
      } else {
        employerOptions = [];
      }
    } catch (err) {
      console.error('Error loading employers:', err);
      employerOptions = [];
    }
  }

  // Function to fetch donation requests
  async function fetchDonationRequests() {
    if (!user) return;
    
    donationsLoading = true;
    
    try {
      const filters: Omit<DonationRequestFilters, 'user_uuid'> = {
        page: donationsCurrentPage,
        per_page: 10,
        lifecycle_stage: selectedStatusBucket
          ? STATUS_BUCKET_LIFECYCLE_STAGES[selectedStatusBucket]
          : undefined,
        employer_uuid: selectedEmployer && selectedEmployer !== '' ? selectedEmployer : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        sort_by: 'created_at',
        sort_direction: 'desc'
      };

      const response = await EmployeeDonationService.getDonationRequests(user.uuid, filters);
      
      if (response && response.success) {
        // Ensure donations is always an array
        donations = Array.isArray(response.data) ? response.data : [];
        
        const p = response.paging;
        if (p && typeof p === 'object') {
          donationsTotalPages = p.last_page ?? 1;
          donationsTotalItems = p.total ?? donations.length;
          if (donationsCurrentPage > (p.last_page ?? 1)) {
            donationsCurrentPage = 1;
          }
        } else {
          donationsTotalPages = 1;
          donationsTotalItems = donations.length;
          donationsCurrentPage = 1;
        }
        
        if (donations.length > 0) {
          filteredDonations = donations.map(donation => ({
            uuid: donation.uuid,
            id: donation.uuid,
            charity_name: donation.charity?.name || 'Unknown Charity',
            charity: donation.charity,
            leave_hours: donation.leave_hours,
            leave_value: donation.leave_value,
            created_at: donation.created_at,
            employer_name: donation.employer?.name || 'Unknown Employer',
            employer: donation.employer,
            date: formatDate(donation.created_at),
            status: donation.donation_request_status?.name || 'Pending',
            lifecycle_stage: donation.lifecycle_stage,
            payment_status: donation.payment_status || 'not_paid',
            donation_request_status: donation.donation_request_status
          }));
        } else {
          filteredDonations = [];
        }
      } else {
        donations = [];
        filteredDonations = [];
      }
    } catch (err) {
      console.error('Error fetching donation requests:', err);
      donations = [];
      filteredDonations = [];
      donationsTotalPages = 1;
      donationsTotalItems = 0;
    } finally {
      donationsLoading = false;
    }
  }

  // Filter functions
  function handleEmployerChange(value: string) {
    selectedEmployer = value;
    donationsCurrentPage = 1;
    fetchDonationRequests();
  }

  function handleDateChange(event: CustomEvent) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
    donationsCurrentPage = 1;
    fetchDonationRequests();
  }

  function handleDonationsPageChange(event: CustomEvent) {
    donationsCurrentPage = event.detail.page;
    fetchDonationRequests();
  }

  function handleRowClick(row: any) {
    if (row.uuid) {
      push(`/my-donation/${row.uuid}`);
    }
  }

  // Table columns
  const columns = [
    {
      key: 'charity_name',
      label: 'Charity Name',
      width: '30%',
      render: (value: string, row: any) => {
        const charityUuid = row.charity?.uuid || '';
        const charityName = value || 'Unknown Charity';
        if (charityUuid) {
          return `<div class="charity-info">
            <button class="charity-name-button" data-charity-uuid="${charityUuid}" type="button" aria-label="View charity details">
              <span class="charity-name">${charityName}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>`;
        } else {
          return `<div class="charity-info">
            <span class="charity-name">${charityName}</span>
          </div>`;
        }
      }
    },
    {
      key: 'employer_name',
      label: 'Employer Name',
      width: '20%',
      render: (value: string) => `<span class="employer-name">${value || 'Unknown Employer'}</span>`
    },
    {
      key: 'leave_hours',
      label: 'Hours',
      width: '90px',
      render: (value: number) => `<span class="hours-value">${value} hours</span>`
    },
    {
      key: 'leave_value',
      label: 'Value',
      width: '100px',
      render: (_value: number, row: any) => formatTableValue(row)
    },
    {
      key: 'date',
      label: 'Date',
      width: '120px',
      render: (value: string) => `<span class="date-value">${value}</span>`
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (_value: string, row: any) => statusBucketBadgeHtmlFromRow(row)
    }
  ];

  // Wire up charity click handlers after table update (status badge is rendered inline).
  afterUpdate(() => {
    // Handle charity name clicks
    const charityButtons = document.querySelectorAll('.charity-name-button');
    charityButtons.forEach((button) => {
      const charityUuid = button.getAttribute('data-charity-uuid');
      if (charityUuid && !button.hasAttribute('data-click-handler')) {
        button.setAttribute('data-click-handler', 'true');
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          push(`/view-charity/${charityUuid}`);
        });
        button.addEventListener('keydown', (e: Event) => {
          const keyEvent = e as KeyboardEvent;
          if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
            keyEvent.preventDefault();
            keyEvent.stopPropagation();
            push(`/view-charity/${charityUuid}`);
          }
        });
      }
    });
  });

  // Load dashboard data on component mount
  onMount(async () => {
    user = AuthService.getUser();
    
    if (!user) {
      error = 'User data not found.';
      return;
    }

    await Promise.all([
      loadDashboardData(),
      loadEmployers()
    ]);

    fetchDonationRequests();
  });

  async function loadDashboardData() {
    try {
      loading = true;
      error = null;

      const result = await EmployeeDashboardService.getDashboardData({ limit: 3 });

      if (result.success) {
        dashboardData = result.data;
      } else {
        error = result.message || "Failed to load dashboard data";
      }
    } catch (err: any) {
      error = err.message || "An unexpected error occurred";
    } finally {
      loading = false;
    }
  }

  async function refreshData() {
    await Promise.all([
      loadDashboardData(),
      fetchDonationRequests()
    ]);
  }

  function handleDonationComplete() {
    refreshData();
    showDonationModal = false;
  }
</script>

<div class="employee-dashboard">
  <!-- Loading State -->
  {#if loading}
    <div class="loading-container">
      <!-- Skeleton Loaders for better perceived performance -->
      <div class="skeleton-header"></div>
      <div class="skeleton-metrics">
        {#each Array(3) as _}
          <div class="skeleton-metric"></div>
        {/each}
      </div>
      <div class="skeleton-table"></div>
    </div>
  {:else if error}
    <!-- Error State -->
    <div class="error-container">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="error-icon">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
      <h3>Unable to Load Donations</h3>
      <p>{error}</p>
      <Button variant="primary" on:click={refreshData}>Try Again</Button>
    </div>
  {:else if dashboardData}
    <!-- Dashboard Content -->

    <!-- New Donation CTA - Prominent -->
    <div class="new-donation-cta" on:click={handleCreateDonation} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && handleCreateDonation()}>
      <div class="cta-content">
        <div class="cta-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </div>
        <div class="cta-text">
          <h2 class="cta-title">New Donation Request</h2>
          <p class="cta-subtitle">Start making a difference today</p>
        </div>
      </div>
      <div class="cta-arrow">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </div>
    </div>

    <!-- Status summary tiles (also filter the table below) -->
    <div class="status-tiles">
      {#each statusBuckets as bucket}
        <button
          type="button"
          class="status-tile"
          class:active={selectedStatusBucket === bucket}
          on:click={() => handleStatusBucketClick(bucket)}
          aria-pressed={selectedStatusBucket === bucket}
        >
          <span class="status-tile-label">{STATUS_BUCKET_LABEL[bucket]}</span>
          <span class="status-tile-value">{formatBucketValue(bucket)}</span>
        </button>
      {/each}
    </div>

    <!-- My Donation Requests Section -->
    <div class="donations-section">
      <div class="section-header">
        <h2 class="section-title">My Donation Requests</h2>
      </div>

      <!-- Filters -->
      <div class="filters-section">
        <div class="filters-left">
          <div class="filter-item">
            <label for="status-filter" class="filter-label">Status</label>
            <Dropdown
              id="status-filter"
              options={statusFilterOptions}
              selectedValue={selectedStatusBucket}
              onSelectionChange={handleStatusFilterChange}
              placeholder="All Statuses"
            />
          </div>
          <div class="filter-item">
            <label for="employer-filter" class="filter-label">Employer</label>
            <Dropdown
              id="employer-filter"
              options={[
                { uuid: '', name: 'All Employers' },
                ...employerOptions
              ]}
              selectedValue={selectedEmployer}
              onSelectionChange={handleEmployerChange}
              placeholder="All Employers"
            />
          </div>
          <div class="filter-item">
            <label for="date-filter" class="filter-label">Date Range</label>
            <DateRange
              bind:startDate
              bind:endDate
              placeholder="Date Range"
              on:change={handleDateChange}
            />
          </div>
        </div>
      </div>

      <!-- Donations Table -->
      {#if donationsLoading}
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading donations...</p>
        </div>
      {:else if filteredDonations.length === 0}
        <div class="no-data-container">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="no-data-icon">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
          <h3>No Donation Requests Found</h3>
          <p>Start making a difference by creating your first donation request.</p>
        </div>
      {:else}
        <div class="table-section">
          <Table 
            columns={columns}
            data={filteredDonations}
            onRowClick={handleRowClick}
          />
        </div>
        {#if donationsTotalPages > 1}
          <div class="pagination-container">
            <Pagination 
              currentPage={donationsCurrentPage} 
              totalPages={donationsTotalPages} 
              on:pageChange={handleDonationsPageChange} 
            />
          </div>
        {/if}
      {/if}
    </div>

    <!-- Donation Request Modal -->
    <DonationRequestModal 
      isOpen={showDonationModal}
      on:close={handleModalClose}
      on:complete={handleDonationComplete}
    />
  {/if}
</div>

<style>
  .employee-dashboard {
    min-height: 100vh;
    background-color: #f7fafc;
    padding: 1.5rem 2rem;
  }

  /* New Donation CTA - Prominent */
  .new-donation-cta {
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
    border: none;
    width: 100%;
  }

  .new-donation-cta:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .new-donation-cta:active {
    transform: translateY(0);
  }

  .new-donation-cta:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.3), 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  .cta-content {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    flex: 1;
  }

  .cta-icon {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
  }

  .cta-text {
    flex: 1;
  }

  .cta-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    margin: 0 0 0.25rem 0;
    line-height: 1.2;
  }

  .cta-subtitle {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-weight: 400;
  }

  .cta-arrow {
    color: white;
    opacity: 0.9;
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }

  .new-donation-cta:hover .cta-arrow {
    transform: translateX(4px);
  }

  /* Loading State - Skeleton Loaders */
  .loading-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .skeleton-header {
    height: 80px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s ease-in-out infinite;
    border-radius: 8px;
  }

  .skeleton-metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }

  .skeleton-metric {
    height: 100px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s ease-in-out infinite;
    border-radius: 8px;
  }

  .skeleton-table {
    height: 200px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s ease-in-out infinite;
    border-radius: 8px;
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  /* Error State */
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
    background: white;
    border-radius: 12px;
    padding: 3rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .error-icon {
    color: #ef4444;
    margin-bottom: 1rem;
  }

  .error-container h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .error-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
    font-size: 0.875rem;
  }

  /* No Data State */
  .no-data-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 12px;
    padding: 4rem 2rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    text-align: center;
  }

  .no-data-icon {
    color: #d1d5db;
    margin-bottom: 1.5rem;
  }

  .no-data-container h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .no-data-container p {
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
    font-size: 0.875rem;
  }

  /* Status summary tiles */
  .status-tiles {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .status-tile {
    background: white;
    border-radius: 12px;
    padding: 1.25rem 1.5rem;
    border: 2px solid #e5e7eb;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  .status-tile:hover {
    border-color: var(--color-primary);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .status-tile:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.25);
  }

  .status-tile.active {
    border-color: var(--color-primary);
    background: #fffbeb;
  }

  .status-tile-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .status-tile-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }


  /* Status Badge Styles */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  :global(.status-approved) {
    background: #d1fae5;
    color: #065f46;
  }

  :global(.status-rejected) {
    background: #fee2e2;
    color: #991b1b;
  }

  :global(.status-pending) {
    background: #fef3c7;
    color: #92400e;
  }

  /* Donations Section */
  .donations-section {
    margin-top: 2rem;
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
    flex-wrap: wrap;
  }

  .filter-item {
    position: relative;
    min-width: 180px;
  }

  .filter-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .table-section {
    overflow: hidden;
  }

  :global(.table-section .data-table th:nth-child(6)),
  :global(.table-section .data-table td:nth-child(6)) {
    text-align: left;
    vertical-align: middle;
    width: 120px;
    min-width: 120px;
    max-width: 120px;
  }

  :global(.value-empty) {
    color: #9ca3af;
  }

  :global(.value-amount) {
    font-weight: 500;
    color: #111827;
  }

  .pagination-container {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: center;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .employee-dashboard {
      padding: 1rem;
    }

    .status-tiles {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }

    .status-tile-value {
      font-size: 1.25rem;
    }

    .section-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .new-donation-cta {
      padding: 1.5rem;
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    .cta-content {
      width: 100%;
    }

    .cta-icon {
      width: 48px;
      height: 48px;
    }

    .cta-title {
      font-size: 1.25rem;
    }

    .cta-arrow {
      align-self: flex-end;
    }

    .error-container,
    .no-data-container {
      padding: 2rem 1rem;
    }

    .skeleton-metrics {
      grid-template-columns: 1fr;
    }

    .filters-left {
      flex-direction: column;
      align-items: stretch;
    }

    .filter-item {
      min-width: 100%;
    }

    .donations-section {
      padding: 1rem;
    }
  }
</style>
